import React, { Suspense, useEffect, useState } from "react";
import { HashRouter, Redirect, Route, Switch } from "react-router-dom";
import { Provider, useSelector } from "react-redux";
import store from "./redux/store";
import { AbilityContext } from "./casl/Can";
import ability from "./casl/Ability.jsx";
import "./App.scss";
import ToastPortal from "./components/ToastPortal";
import { PersistGate } from "redux-persist/integration/react";
import { persistStore } from "redux-persist";
import GoogleMapsProvider from "./components/layouts/GoogleMapsProvider/GoogleMapsProvider";
import "react-dates/initialize";
import "react-dates/lib/css/_datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import routes from "./routes";
import { wording } from "./constants/constants";

const persistor = persistStore(store);

const NotFound = () => {
  const [show, setShow] = useState(false);
  useEffect(()=>{
    setTimeout(()=>{
      setShow(true);
    }, 1000)
  },[])
  return (
    <div>
      {show?<>
        <h1>404 - Page Not Found</h1>
        <p>The page you are looking for does not exist.</p>
      </>:null}
      {/* You can add a link to the homepage here */}
    </div>
  );
};

const PrivateRoute = ({ children, ...rest }) => {
  const { authStore } = useSelector((state) => ({
    authStore: state.authStore,
  }));

  return (
    <Route
      {...rest}
      render={({ location }) =>
        authStore.authData ? (
          children
        ) : (
          <Redirect
            to={{
              pathname: "/login",
              state: { from: location },
            }}
          />
        )
      }
    />
  );
};

const NestedRoute = (props) => {
  const { authStore } = useSelector((state) => ({
    authStore: state.authStore,
  }));
  const onRoleName = () => {
    const role = !!authStore.authData &&
      !!authStore.authData.result &&
      !!authStore.authData.result.id_type_company
      ? authStore.authData.result.id_type_company === 4
        ? "goodsTraderIntercompanyRoutes.jsx"
        : [1, 4, 5].findIndex(
            (item) => item === authStore.authData.result.id_roles
          ) >= 0
        ? "routes.js"
        : [2].findIndex(
            (item) => item === authStore.authData.result.id_roles
          ) >= 0
        ? "managerRoutes.jsx"
        : [3].findIndex(
            (item) => item === authStore.authData.result.id_roles
          ) >= 0
        ? "securityRoutes.jsx"
        : [6, 7].findIndex(
            (item) => item === authStore.authData.result.id_roles
          ) >= 0
        ? "mechanicRoutes.jsx"
        : [13].findIndex(
            (item) => item === authStore.authData.result.id_roles
          ) >= 0
        ? "hrRoutes.jsx"
        : [14].findIndex(
            (item) => item === authStore.authData.result.id_roles
          ) >= 0
        ? "maintenanceRoutes.jsx"
        : "guests"
      : "guests";
      return  role
  };
  return (
    <>
      <Switch>
        {props.routes
          .filter((item) => !!!item.private)
          .filter((subitem) =>subitem.files.findIndex((i) => i === onRoleName()) >= 0)
          .map(({ Component, ...item }, key) => (
            <Route
              key={key}
              path={item.path}
              render={(rest) => (
                <>
                  {!!item.children &&
                  item.children.filter(
                    (subitem) =>
                      subitem.files.findIndex((i) => i === onRoleName()) >= 0
                  ).length > 0 ? (
                    <Suspense
                      fallback={
                        <div className="animated fadeIn pt-1 text-center">
                          <div className="sk-spinner sk-spinner-pulse"></div>
                        </div>
                      }
                    >
                      <Component
                        {...rest}
                        breadcrumb={item.children.filter(
                          (subitem) =>
                            subitem.files.findIndex(
                              (i) => i === onRoleName()
                            ) >= 0
                        )}
                      >
                        <NestedRoute
                          routes={item.children.filter(
                            (subitem) =>
                              subitem.files.findIndex(
                                (i) => i === onRoleName()
                              ) >= 0
                          )}
                        />
                      </Component>
                    </Suspense>
                  ) : (
                    <Suspense
                      fallback={
                        <div className="animated fadeIn pt-1 text-center">
                          <div className="sk-spinner sk-spinner-pulse"></div>
                        </div>
                      }
                    >
                      <Component {...rest} />
                    </Suspense>
                  )}
                </>
              )}
              exact={!!item.exact}
            ></Route>
          ))}
        {props.routes
          .filter((item) => !!item.private)
          .filter((subitem) =>subitem.files.findIndex((i) => i === onRoleName()) >= 0)
          .map(({ Component, ...item }, key) => (
            <PrivateRoute key={key} path={item.path}>
              <>
                {!!item.children &&
                item.children.filter(
                  (subitem) =>
                    subitem.files.findIndex((i) => i === onRoleName()) >= 0
                ).length > 0 ? (
                  <Component
                    breadcrumb={item.children.filter(
                      (subitem) =>
                        subitem.files.findIndex((i) => i === onRoleName()) >= 0
                    )}
                  >
                    <Suspense
                      fallback={
                        <div className="animated fadeIn pt-1 text-center">
                          <div className="sk-spinner sk-spinner-pulse"></div>
                        </div>
                      }
                    >
                      <NestedRoute
                        routes={item.children.filter(
                          (subitem) =>
                            subitem.files.findIndex(
                              (i) => i === onRoleName()
                            ) >= 0
                        )}
                      />
                    </Suspense>
                  </Component>
                ) : (
                  <Suspense
                    fallback={
                      <div className="animated fadeIn pt-1 text-center">
                        <div className="sk-spinner sk-spinner-pulse"></div>
                      </div>
                    }
                  >
                    <Component />
                  </Suspense>
                )}
              </>
            </PrivateRoute>
          ))}
        <Route path="*" component={NotFound} />
      </Switch>
    </>
  );
};
const App = () => {
  useEffect(() => {
    document.title = wording["Automa IoT Platform"];

    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.getElementsByTagName("head")[0].appendChild(link);
    }

    link.href = wording["icon-title"];
  }, []);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <AbilityContext.Provider value={ability}>
          <GoogleMapsProvider>
            <HashRouter>
              <Suspense
                fallback={
                  <div className="animated fadeIn pt-1 text-center">
                    <div className="sk-spinner sk-spinner-pulse"></div>
                  </div>
                }
              >
                <NestedRoute routes={routes} />
              </Suspense>
            </HashRouter>
          </GoogleMapsProvider>
        </AbilityContext.Provider>
        <ToastPortal />
      </PersistGate>
    </Provider>
  );
};

export default App;
